import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { ApiFehler, hole } from '../lib/api'
import { darfSehen } from './logic'

interface AktiveSitzung {
  setupRequired: false
  user: { id: string; name: string; email: string | null }
  member: { id: string; displayName: string; role: string; color: string; householdId: string }
  household: { id: string; name: string; timezone: string }
  capabilities: string[]
  preview: boolean
}

interface EinrichtungNoetig {
  setupRequired: true
  user: { id: string; name: string; email: string | null }
}

export type MePayload = AktiveSitzung | EinrichtungNoetig

interface SitzungWert {
  abfrage: UseQueryResult<MePayload, ApiFehler>
  caps: Set<string> | undefined
}

const Kontext = createContext<SitzungWert | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const abfrage = useQuery<MePayload, ApiFehler>({
    queryKey: ['me'],
    queryFn: () => hole<MePayload>('/api/auth/me'),
    // Ein 401 ist keine Störung, sondern die Antwort „nicht angemeldet“ -
    // Wiederholen würde nur Zeit kosten.
    retry: (anzahl, fehler) => fehler.angemeldet && anzahl < 2,
    staleTime: 30_000,
  })
  const caps =
    abfrage.data && !abfrage.data.setupRequired ? new Set(abfrage.data.capabilities) : undefined
  return <Kontext.Provider value={{ abfrage, caps }}>{children}</Kontext.Provider>
}

export function useSitzung(): SitzungWert {
  const wert = useContext(Kontext)
  if (!wert) throw new Error('useSitzung braucht einen SessionProvider')
  return wert
}

/**
 * ⚠️ Rein kosmetisch. Zu jedem Aufruf existiert die serverseitige Entsprechung;
 * hier wird nur versteckt, was ohnehin abgewiesen würde.
 */
export function useDarf(capability: string): boolean {
  const { caps } = useSitzung()
  return darfSehen(caps, capability)
}

export function Darf({ was, children }: { was: string; children: ReactNode }) {
  return useDarf(was) ? <>{children}</> : null
}
